import FilterHorizontal from 'assets/icons/revamp/filter_horizontal';
import AddOutlined from 'assets/icons/revamp/add-outlined';
import BaseButton from 'components/ui/BaseButton';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import FilterDaysOfWeekRevamp from './FilterDaysOfWeekRevamp';
import FilterPriorityRevamp from './FilterPriorityRevamp';
import { updateFilterSearchWord } from 'store/reducer/filter/slice';
import {
  MORNING_ROUTINE_TAB_INDEX,
  SETTING_TAB_INDEX,
  TAB
} from 'constants/general';
import { Collapsible, CollapsibleContent } from 'components/ui/Collapsible';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import CloseCircle from 'assets/icons/CloseCircle';
import EveningRoutineStartTimeRevamp from '../../timing/EveningRoutineStartTimeRevamp';
import BreakFrequency from '../../timing/BreakFrequency';
import { updateIsShowRoutineModal } from 'store/reducer/setting/slice';
import { PackEditorTabMode } from 'enums';
import { tabBodyHeaderRevampSelector } from 'store/reducer/setting/selectors';
import Gear from 'assets/icons/Gear';

interface TabBodyHeaderRevampProps {
  onAddHabit?: () => void;
  onFilter?: () => void;
  className?: string;
}

const TabBodyHeaderRevamp: React.FC<TabBodyHeaderRevampProps> = ({
  onAddHabit,
  onFilter,
  className = ''
}) => {
  const dispatch = useAppDispatch();
  const {
    isSettingToolbarShown,
    activeTabIndex,
    tabs,
    searchWord,
    packEditorTabMode
  } = useAppSelectorShallow(tabBodyHeaderRevampSelector);

  const showRoutineTimingControls = packEditorTabMode === null;

  return (
    <div className={`w-full flex flex-col ~gap-2/3  ${className}`}>
      <div className='w-full flex items-center justify-between ~gap-4/6 ~my-2/2.5'>
        <BaseButton
          title={t('settings_page.add_habits')}
          icon={
            <AddOutlined
              className='~w-3.5/4 h-auto text-primary-400 dark:text-primary-dark-500'
              fill='currentColor'
              fillDarkMode='currentColor'
            />
          }
          onClick={() => onAddHabit?.()}
          variant='secondary'
          aria-label={t('settings_page.add_habits')}
          size='xs'
        />
        <>
          {showRoutineTimingControls &&
            [TAB.EVENING, TAB.MICRO_BREAKS].includes(tabs[activeTabIndex]) &&
            (activeTabIndex === SETTING_TAB_INDEX.MICRO_BREAKS ? (
              <BreakFrequency />
            ) : (
              <EveningRoutineStartTimeRevamp />
            ))}
        </>
        <div className='flex items-center ~gap-2/4'>
          {packEditorTabMode === PackEditorTabMode.ROUTINE &&
            activeTabIndex === MORNING_ROUTINE_TAB_INDEX && (
              <BaseButton
                title={t('advanced')}
                onClick={() => dispatch(updateIsShowRoutineModal(true))}
                data-testid='advanced-settings-button'
                variant='secondary'
                size='xs'
                icon={
                  <Gear
                    styles='w-auto ~h-3.5/4 text-primary-400 dark:text-primary-dark-500'
                    fill='currentColor'
                    fillDarkMode='currentColor'
                  />
                }
              />
            )}
          <BaseButton
            title={isSettingToolbarShown ? '' : t('settings_page.filter')}
            icon={
              isSettingToolbarShown ? (
                <CloseCircle
                  styles='w-auto ~h-4/5 text-primary-400 dark:text-primary-dark-500'
                  fill='currentColor'
                  fillDarkMode='currentColor'
                />
              ) : (
                <FilterHorizontal
                  className='w-auto ~h-3.5/4 text-primary-400 dark:text-primary-dark-500'
                  fill='currentColor'
                  fillDarkMode='currentColor'
                />
              )
            }
            onClick={() => onFilter?.()}
            variant='secondary'
            aria-label={t('settings_page.filter')}
            aria-expanded={isSettingToolbarShown}
            size='xs'
          />
        </div>
      </div>
      <Collapsible
        open={isSettingToolbarShown}
        onOpenChange={() => onFilter?.()}
        className='w-full min-w-0 rounded-md shadow-sm ~-mt-2/3'>
        <CollapsibleContent className='w-full min-w-0 overflow-x-auto transition-all duration-200 data-[state=closed]:max-h-0 data-[state=open]:max-h-[2000px] scrollbar-thin-custom scroll-mt-1 animate-fadeInSlideDown'>
          <div className='flex w-max min-w-max flex-nowrap items-center justify-start ~gap-x-4/8 ~gap-y-2/3 ~p-2/3 sm:w-full sm:justify-end'>
            <FloatingLabelInput
              type='text'
              placeholder={t('settings_page.search_habit_name')}
              label=''
              value={searchWord}
              onChange={({
                target: { value }
              }: React.ChangeEvent<HTMLInputElement>) => {
                dispatch(updateFilterSearchWord(value));
              }}
              size='small'
              inputClassName='!~text-[0.55rem]/xs w-auto'
              containerClassName='min-w-[300px] sm:~w-60/96'
            />
            <FilterPriorityRevamp />
            {TAB.MICRO_BREAKS !== tabs[activeTabIndex] && (
              <FilterDaysOfWeekRevamp />
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default TabBodyHeaderRevamp;
